import { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

import { api } from "../lib/api";
import type { PairingCode, PairingStatus } from "../lib/types";

/**
 * Pair a phone by pointing its camera at this screen.
 *
 * What is in the QR matters more than the QR. It is *not* BOOTSTRAP_TOKEN: that secret is
 * permanent and opens enrolment for anyone who learns it, so rendering it as a machine-readable
 * image on a screen would be worse than reading it aloud -- a photograph keeps working. The QR
 * carries a pairing code that lives five minutes, works once, and dies when this dialog closes.
 *
 * The code sits in the URL fragment, which browsers never send to a server, so it cannot reach
 * an access log even if someone opens the link by hand.
 */
export default function PairDevice({ onPaired }: { onPaired: () => void }) {
  const [pairing, setPairing] = useState<PairingCode | null>(null);
  const [status, setStatus] = useState<PairingStatus | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canvas = useRef<HTMLCanvasElement | null>(null);

  const mint = useCallback(async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const created = await api.createPairingCode();
      setPairing(created);
      setRemaining(created.expires_in);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void mint();
  }, [mint]);

  // Draw the QR whenever a new code arrives. High correction level so it still scans from a
  // phone held at an angle across a desk, which is how this will actually be used.
  useEffect(() => {
    if (!pairing || !canvas.current) return;
    void QRCode.toCanvas(canvas.current, pairing.qr_payload, {
      width: 232,
      margin: 1,
      errorCorrectionLevel: "H",
      color: { dark: "#000000", light: "#ffffff" },
    }).catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [pairing]);

  // Poll until the phone claims it. Cheap, and it means the user is told it worked rather than
  // having to guess from the other device.
  useEffect(() => {
    if (!pairing || status?.state === "claimed") return;
    const timer = window.setInterval(async () => {
      try {
        const next = await api.pairingStatus(pairing.id);
        setStatus(next);
        setRemaining(next.expires_in);
        if (next.state === "claimed") {
          window.clearInterval(timer);
          onPaired();
        }
      } catch {
        /* a dropped poll is not worth surfacing; the next one will tell the same story */
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [pairing, status?.state, onPaired]);

  // Local countdown so the number moves every second rather than every poll.
  useEffect(() => {
    if (!remaining || status?.state === "claimed") return;
    const timer = window.setTimeout(() => setRemaining((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [remaining, status?.state]);

  // Closing the dialog revokes an unclaimed code. A pairing code left alive is a door left open.
  useEffect(() => {
    return () => {
      if (pairing && status?.state !== "claimed") void api.cancelPairingCode(pairing.id);
    };
  }, [pairing, status?.state]);

  const claimed = status?.state === "claimed";
  const expired = remaining <= 0 && !claimed && pairing !== null;

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div>
        <h2 style={{ fontSize: 15, margin: 0 }}>Pair the Magpie app</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Open Magpie, choose <strong>Pair with server</strong>, and scan this. The code expires
          in five minutes and works once.
        </p>
      </div>

      {error && <div className="error">{error}</div>}

      {claimed ? (
        <div className="card stack" style={{ gap: 6, alignItems: "center", padding: 24 }}>
          <div style={{ fontSize: 32 }}>✓</div>
          <strong>{status?.claimed_device_name ?? "Device"} paired</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            It has its own token now. This code is spent.
          </span>
        </div>
      ) : (
        <div className="row" style={{ gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div
            style={{
              background: "#fff",
              padding: 10,
              borderRadius: 8,
              lineHeight: 0,
              opacity: expired ? 0.25 : 1,
            }}
          >
            <canvas ref={canvas} aria-label="Pairing QR code" />
          </div>
          <div className="stack" style={{ gap: 8, minWidth: 210 }}>
            <div>
              <div className="eyebrow">Or type this in</div>
              <div
                className="mono"
                style={{ fontSize: 19, letterSpacing: 1, opacity: expired ? 0.4 : 1 }}
              >
                {pairing?.code ?? "----"}
              </div>
            </div>
            <div>
              <div className="eyebrow">Server</div>
              <div className="mono" style={{ fontSize: 12 }}>
                {pairing?.server_url ?? "--"}
              </div>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {expired ? "Expired." : `Expires in ${formatSeconds(remaining)}.`}
            </div>
          </div>
        </div>
      )}

      <div className="row" style={{ gap: 8 }}>
        {!claimed && (
          <button className="ghost" onClick={() => void mint()} disabled={busy}>
            {expired ? "New code" : "Regenerate"}
          </button>
        )}
      </div>
    </div>
  );
}

function formatSeconds(total: number): string {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
}
